'use client';

import { useRef, useEffect, useCallback } from 'react';
import Script from 'next/script';
import { styled, css } from 'styled-components';
import * as Sentry from '@sentry/nextjs';
import { HUBSPOT_PORTAL_ID } from '@lib/constants';
import { bounce } from './Button';

type HubSpotFormProps = {
  /**
   * The formId of the HubSpot form found in the embed code provided by HubSpot
   */
  formId: string;
  /**
   * Optional form title
   */
  title?: string;
  /**
   * The color scheme to use for the form
   * - dark: Uses white colors to stand out against a dark background such as with the CTA module
   * - default: Uses global colors from CSS variables
   * @default 'default'
   */
  formColorScheme?: 'dark' | 'default';
  /**
   * Optional className
   */
  className?: string;
};

const labelStyles = css<{ $colorScheme: 'dark' | 'default' }>`
  color: ${({ $colorScheme, theme }) =>
    $colorScheme === 'dark'
      ? theme.colors.white
      : 'var(--global-form-label-color)'};
  font-family: var(--font-inter) !important;
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm} !important;
  font-weight: ${({ theme }) => theme.typography.fontWeight.body};
  margin-block-end: ${({ theme }) => theme.spacing[0.25]};

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) =>
      theme.typography.fontSize.desktop.bodySm} !important;
  }
`;

const inputStyles = css<{ $colorScheme: 'dark' | 'default' }>`
  border: ${({ $colorScheme, theme }) =>
    $colorScheme === 'dark'
      ? `1px solid ${theme.colors.blue800}`
      : '1px solid var(--global-form-border-color)'};
  border-radius: 2.25rem;
  box-shadow: 0px 0px 0px 0px transparent;
  color: ${({ $colorScheme, theme }) =>
    $colorScheme === 'dark'
      ? theme.colors.white
      : 'var(--global-form-label-color)'};
  font-family: var(--font-inter);
  font-size: ${({ theme }) => theme.typography.fontSize.bodyMd};
  font-weight: ${({ theme }) => theme.typography.fontWeight.body};
  height: unset;
  line-height: ${({ theme }) => theme.typography.lineHeight.body};
  margin-block-start: ${({ theme }) => theme.spacing[0.5]};
  margin-block-end: 0;
  padding-block-start: ${({ theme }) => theme.spacing[0.75]} !important;
  padding-block-end: ${({ theme }) => theme.spacing[0.75]} !important;
  padding-inline-start: ${({ theme }) => theme.spacing[1.5]} !important;
  transition:
    box-shadow 333ms ease-in-out,
    border 333ms ease-in-out;
  width: 100%;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodyMd};
    padding-inline-start: ${({ theme }) => theme.spacing[1.5]} !important;
  }
`;

const hoverBoxShadow = css`
  // custom per design
  box-shadow: 0px 4px 24px 0px hsla(230, 78%, 53%, 0.2);
`;

const inputFocusStyles = css<{ $colorScheme: 'dark' | 'default' }>`
  border: 1px solid var(--global-form-hover-color);
  box-shadow: ${({ theme }) =>
    `0px 6px 24px 0px ${theme.colors.dropShadowDarkBlue}`};
  outline: none;
`;

const errorStyles = css<{ $colorScheme: 'dark' | 'default' }>`
  color: ${({ $colorScheme, theme }) =>
    $colorScheme === 'dark'
      ? theme.colors.orange300
      : 'var(--global-error-msg-color)'} !important;
  font-family: var(--font-inter);
  font-size: ${({ theme }) => theme.typography.fontSize.bodyXs} !important;
  font-weight: ${({ theme }) => theme.typography.fontWeight.body};
  margin-block-end: 0px !important;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) =>
      theme.typography.fontSize.desktop.bodyXs} !important;
  }
`;

const buttonStyles = css`
  background: var(--global-link-color) !important;
  border: 3px solid var(--global-link-color) !important;
  border-radius: ${({ theme }) => theme.radius.btn};
  color: ${({ theme }) => theme.colors.white};
  display: inline-block;
  font-family: var(--font-walsheim) !important;
  font-size: ${({ theme }) => theme.typography.fontSize.button} !important;
  font-weight: ${({ theme }) => theme.typography.fontWeight.button} !important;
  line-height: ${({ theme }) => theme.typography.lineHeight.button} !important;
  outline: 0.05em solid var(--global-link-color) !important;
  outline-offset: -0.05em !important;
  padding-block: ${({ theme }) => theme.spacing[0.75]} !important;
  padding-inline: ${({ theme }) => theme.spacing[1.5]} !important;
  position: relative;
  white-space: nowrap;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) =>
      theme.typography.fontSize.desktop.button} !important;
    padding-block: ${({ theme }) => theme.spacing[1]} !important;
    padding-inline: ${({ theme }) => theme.spacing[2]} !important;
  }
`;

const HubSpotFormWrapper = styled.div<{
  $colorScheme: 'dark' | 'default';
}>`
  margin-block: ${({ theme }) => theme.spacing[2]};
  fieldset.form-columns-2 {
    max-width: unset;

    .hs-form-field input {
      width: calc(100% - ${({ theme }) => theme.spacing[0.75]});
    }
  }

  .hs_error_rollup {
    ${errorStyles}
    ul {
      list-style: none;
      padding: 0;
      li label {
        ${errorStyles}
      }
    }
  }

  // Set up container queries for two column form layouts
  & fieldset.form-columns-2 {
    container-type: inline-size;
  }

  // move fields to be one line when containier is smaller than 350px
  @container (max-width: 350px) {
    & .hs-form-field {
      display: block;
      width: 100% !important;
    }
  }

  .hs-form-field {
    position: relative;
    margin-block-start: ${({ theme }) => theme.spacing[0.75]};
    margin-block-end: ${({ theme }) => theme.spacing[1.5]};

    /**
     * Error messaging
     */
    & ul.hs-error-msgs {
      list-style: none;
      padding: 0;
      margin: 0;

      & label.hs-error-msg {
        ${errorStyles}
        display: block;
        position: absolute;
        left: 0;
      }

      &:first-of-type label.hs-error-msg {
        top: 100%;
        margin-top: ${({ theme }) => theme.spacing[0.5]};
      }

      &:not(:first-of-type) label.hs-error-msg {
        top: calc(100% + ${({ theme }) => theme.spacing[2]});
      }
    }

    /**
     * Labels
     */
    & label,
    & label:not(.hs-error-msg),
    & .hs-form-required {
      ${labelStyles}
    }

    /**
    * Inputs
    */

    // Select input
    & .input select {
      &.hs-input {
        ${inputStyles}
        appearance: none;
        position: relative;
        // The wistia UI icon for Caret Down Icon as a data URL - if this icon ever changes be sure to update it here as well
        background-image: url('data:image/svg+xml,%0A%3Csvg%20viewBox%3D%220%200%2024%2024%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%0A%20%20%3Cpath%20d%3D%22M11.9999%2019.3331C11.7439%2019.3331%2011.4879%2019.2358%2011.2932%2019.0398L2.95992%2010.7065C2.56926%2010.3158%202.56926%209.68251%202.95992%209.29184C3.35059%208.90117%203.98392%208.90117%204.37459%209.29184L12.0012%2016.9185L19.6279%209.29184C20.0185%208.90117%2020.6519%208.90117%2021.0425%209.29184C21.4332%209.68251%2021.4332%2010.3158%2021.0425%2010.7065L12.7092%2019.0398C12.5146%2019.2345%2012.2586%2019.3331%2012.0026%2019.3331H11.9999Z%22%20fill%3D%22currentColor%22/%3E%0A%3C/svg%3E%0A');
        background-position: right ${({ theme }) => theme.spacing[1.5]} center;
        background-repeat: no-repeat;
        background-size: ${({ theme }) => theme.spacing[1.5]};
        background-color: ${({ theme }) => theme.colors.white};
        color: ${({ $colorScheme, theme }) =>
          $colorScheme === 'dark'
            ? theme.colors.blue800
            : 'var(--global-form-select-color)'};
        padding-inline-end: ${({ theme }) => theme.spacing[3.75]};

        // Error state of select
        &.invalid {
          border-color: ${({ $colorScheme, theme }) =>
            $colorScheme === 'dark'
              ? theme.colors.orange500
              : 'var(--global-error-border-color)'};
        }
      }
      &.hs-input:hover {
        ${hoverBoxShadow}
      }

      &.hs-input:focus {
        ${inputFocusStyles}
        color: ${({ theme }) => theme.colors.blue500};
        background-image: url('data:image/svg+xml,%0A%3Csvg%20viewBox%3D%220%200%2024%2024%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%0A%20%20%3Cpath%20d%3D%22M11.9999%2019.3331C11.7439%2019.3331%2011.4879%2019.2358%2011.2932%2019.0398L2.95992%2010.7065C2.56926%2010.3158%202.56926%209.68251%202.95992%209.29184C3.35059%208.90117%203.98392%208.90117%204.37459%209.29184L12.0012%2016.9185L19.6279%209.29184C20.0185%208.90117%2020.6519%208.90117%2021.0425%209.29184C21.4332%209.68251%2021.4332%2010.3158%2021.0425%2010.7065L12.7092%2019.0398C12.5146%2019.2345%2012.2586%2019.3331%2012.0026%2019.3331H11.9999Z%22%20fill%3D%22%232949E5%22/%3E%0A%3C/svg%3E%0A');
      }
    }

    // text inputs
    & input {
      &.hs-input {
        ${inputStyles}
      }
      &.hs-input:hover {
        ${hoverBoxShadow}
      }

      &.hs-input:focus,
      &.hs-input:focus-visible {
        ${inputFocusStyles}
      }

      &.hs-input.error {
        border-color: ${({ $colorScheme, theme }) =>
          $colorScheme === 'dark'
            ? theme.colors.orange500
            : 'var(--global-error-border-color)'};
        box-shadow: none;
      }
    }

    // Checkboxes and text area inputs
    & .input {
      & .hs-input {
        ${inputStyles}
        &.hs-fieldtype-textarea {
          ${inputStyles}
          border-radius: ${({ theme }) => theme.radius.std};
          width: calc(100% - ${({ theme }) => theme.spacing[2]}) !important;
          max-width: unset !important;
          min-height: ${({ theme }) => theme.spacing[5]} !important;

          // Error state of textarea
          &.invalid {
            border-color: ${({ $colorScheme, theme }) =>
              $colorScheme === 'dark'
                ? theme.colors.orange500
                : 'var(--global-error-border-color)'};
          }
        }
        &.hs-fieldtype-textarea:hover {
          ${hoverBoxShadow}
        }
        &.hs-fieldtype-textarea:focus {
          ${inputFocusStyles}
        }
      }

      & ul.inputs-list {
        list-style: none;
        padding: 0;
        margin: 0;
        & li {
          margin-block: 10px;
          & label {
            display: flex;
            align-items: center;
            & input {
              border-radius: ${({ theme }) => theme.spacing[0.25]};
              height: 20px;
              margin-inline-end: ${({ theme }) => theme.spacing[0.75]};
              width: 20px;
            }
          }
        }
      }
    }
  }

  /**
  * Submit Button
  * ---
  * Since we can't use our button we must mimic ours
  * Any changes to our button must also be added here
  */

  .hs-submit {
    margin-block-start: ${({ theme }) => theme.spacing[1.5]};

    .hs-button {
      ${buttonStyles}
      box-shadow: 0 0 0 0 transparent !important;
    }

    & .actions {
      background: color-mix(
        in srgb,
        var(--global-link-color),
        ${({ theme }) => theme.colors.black} 55%
      );
      border-radius: ${({ theme }) => theme.radius.btn};
      border-width: 0;
      display: inline-block;
      margin: 0;
      padding: 0;
      width: fit-content;
    }

    & .hs-button:hover:not(.inactive) {
      ${buttonStyles}
      animation: ${bounce} 0.25s ease-out 1ms forwards;
      box-shadow: 0 0.35em 0.35em 0
        color-mix(
          in srgb,
          var(--global-link-color),
          ${({ theme }) => theme.colors.black} 55%
        ) !important;
    }

    & .hs-button:active:not(.inactive):not(.link) {
      box-shadow: 0 0.35em 0.35em 0
        color-mix(
          in srgb,
          var(--global-link-color),
          ${({ theme }) => theme.colors.black} 55%
        );
      top: 2px;
    }
  }

  .stacked .actions {
    margin: 0 !important;
    padding: 0 !important;
  }

  /**
  * Legal Consent/Captcha Sections
  */
  .legal-consent-container,
  .hs-recaptcha {
    margin-block: ${({ theme }) => theme.spacing[1]};
  }
`;

const FormTitle = styled.h3<{ $colorScheme: 'dark' | 'default' }>`
  color: ${({ $colorScheme, theme }) =>
    $colorScheme === 'dark' ? theme.colors.white : 'var(--global-color)'};
`;

/**
 * HubSpot Form Component
 *
 * ## Rules
 * We should never have two instances of the same form on a single page.
 * HubSpot will overwrite the first form with the second form.
 *
 * ## Themes
 * In the HubSpot form builder there are two themes in play which results in two different styled forms.
 * There is old theme that we can no longer access or change and a newer more modern theme.
 * The newer theme is rarely used as most of the forms we'll be using have been crafted using
 * the old theme.
 *
 * ## iFrames
 * Depending on how the form is built in HubSpot some forms render an iFrame. See the examples in the iFrame form and the Multi-step form.
 * As of this writing we are not able to style these forms. More to come on that...
 *
 * ## Color Schemes
 * The form supports two color schemes. Use the `formColorScheme` prop to specify which to use:
 * - `default` (default) - Uses global colors from CSS variables
 * - `dark` - Uses white colors for dark backgrounds like CTA module
 */
const HubSpotForm = ({
  formId,
  title,
  formColorScheme = 'default',
  className,
}: HubSpotFormProps) => {
  // This is to track and prevent multiple form creations
  const hasInitialized = useRef(false);
  const formContainerRef = useRef(null);
  const uniqueFormId = `hubspotForm-${formId}`;

  const initializeForm = useCallback(() => {
    if (!window.hbspt || hasInitialized.current || !formContainerRef.current) {
      return;
    }
    try {
      window.hbspt.forms.create({
        portalId: HUBSPOT_PORTAL_ID,
        formId,
        target: `#${formContainerRef.current.id}`,
      });
      hasInitialized.current = true;
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [formId]);

  useEffect(() => {
    initializeForm();
  }, [initializeForm]);

  return (
    <div className={className}>
      <Script
        onLoad={() => {
          initializeForm();
        }}
        src="https://js.hsforms.net/forms/v2.js"
        strategy="lazyOnload"
      />
      {title ? (
        <FormTitle $colorScheme={formColorScheme}>{title}</FormTitle>
      ) : null}
      <HubSpotFormWrapper
        $colorScheme={formColorScheme}
        id={uniqueFormId}
        ref={formContainerRef}
      />
    </div>
  );
};

export default HubSpotForm;
